import React, { useState, useEffect, useRef } from "react";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import InputLabel from "@mui/material/InputLabel";
import FormControl from "@mui/material/FormControl";
import CircularProgress from "@mui/material/CircularProgress";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import Chip from "@mui/material/Chip";
import Button from "@mui/material/Button";

import SeasonSequenceBucketList from "./SeasonSequenceBucketList";
import SeasonSequenceEmptyState from "./SeasonSequenceEmptyState";
import SeasonSequenceMatrix from "./SeasonSequenceMatrix";
import RollForwardDialog from "./RollForwardDialog";
import { getTrackingYears, getBuckets, getReviewList, getCalendarMatrix } from "../services/seasonSequenceApi";

export default function FranchiseCalendars(props) {
  const comcodes = props.comcodes || [];

  const [selectedComCode, setSelectedComCode] = useState(null);
  const [trackingYears, setTrackingYears] = useState([]);
  const [selectedTrackingYear, setSelectedTrackingYear] = useState("");
  const [buckets, setBuckets] = useState([]);

  const [loadingYears, setLoadingYears] = useState(false);
  const [loadingBuckets, setLoadingBuckets] = useState(false);
  const [yearsError, setYearsError] = useState(null);
  const [bucketsError, setBucketsError] = useState(null);

  const [reviewList, setReviewList] = useState([]);
  const [reviewExpanded, setReviewExpanded] = useState(false);
  const [rollForwardOpen, setRollForwardOpen] = useState(false);
  const [matrix, setMatrix] = useState([]);
  const [matrixExpanded, setMatrixExpanded] = useState(true);

  // When navigating from the review list, the franchise-change effect below
  // resets selectedTrackingYear to "" and reloads years, which would clobber a
  // year set in the same tick. Stash the desired year in a ref and apply it once
  // that franchise's years finish loading (ref, not state, to dodge stale
  // closures in the uncancelled fetch below).
  const pendingYearRef = useRef(null);
  // The editor section, for scroll-into-view after a matrix-cell selection (the editor sits above
  // the matrix, so a click lower on the page can leave it offscreen). pendingScrollRef gates the
  // scroll to matrix-driven selections only -- manual picker changes never move the viewport.
  const editorRef = useRef(null);
  const pendingScrollRef = useRef(false);

  // Load the >5-bucket review queue. Non-fatal: a failure just hides the panel
  // (the picker still works), matching the app's fire-and-forget posture. Kept as
  // a callable so a successful roll-forward can refresh it.
  const loadReviewList = () => {
    getReviewList()
      .then((result) => setReviewList(result.calendars || []))
      .catch(() => setReviewList([]));
  };
  useEffect(() => {
    loadReviewList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Calendar overview matrix (fleet-wide active bucket counts). Loaded once; fire-and-forget like
  // the review list. Refreshed after bucket mutations / roll-forward so the cell counts stay accurate.
  const loadMatrix = () => {
    getCalendarMatrix()
      .then((result) => setMatrix(result.calendars || []))
      .catch(() => setMatrix([]));
  };
  useEffect(() => {
    loadMatrix();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetches trackingYears for the current franchise. Used both by the
  // selection-driven effect below and as part of the refetch triggered
  // after a successful clone/start-with-defaults (the just-configured
  // year's has_buckets needs to flip to true). Simple, uncancelled fetch --
  // same posture as loadBuckets below.
  const loadTrackingYears = () => {
    if (!selectedComCode) {
      setTrackingYears([]);
      return;
    }

    setLoadingYears(true);
    setYearsError(null);

    getTrackingYears(selectedComCode)
      .then((result) => {
        setTrackingYears(result.trackingYears || []);
        // Apply a year requested via the review list, now that its options exist.
        if (pendingYearRef.current != null) {
          setSelectedTrackingYear(pendingYearRef.current);
          pendingYearRef.current = null;
        }
      })
      .catch((err) => {
        setYearsError(err.message);
        setTrackingYears([]);
      })
      .finally(() => {
        setLoadingYears(false);
      });
  };

  // Load trackingYears whenever the selected franchise changes.
  useEffect(() => {
    setSelectedTrackingYear("");
    setBuckets([]);
    setBucketsError(null);
    loadTrackingYears();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedComCode]);

  // Fetches buckets for the current franchise/trackingYear selection.
  // Used both by the selection-driven effect below and as a refetch
  // callback passed down to SeasonSequenceBucketList after a successful
  // rename -- kept as a simple, uncancelled fetch to match the rest of
  // the app's fire-and-forget apiFetch pattern (no other component here
  // guards against stale responses either).
  const loadBuckets = () => {
    if (!selectedComCode || !selectedTrackingYear) return;

    setLoadingBuckets(true);
    setBucketsError(null);

    getBuckets(selectedComCode, selectedTrackingYear)
      .then((result) => {
        setBuckets(result.buckets || []);
      })
      .catch((err) => {
        setBucketsError(err.message);
        setBuckets([]);
      })
      .finally(() => {
        setLoadingBuckets(false);
      });
  };

  // Load buckets whenever the selected franchise/trackingYear changes.
  useEffect(() => {
    setBuckets([]);
    setBucketsError(null);
    loadBuckets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedComCode, selectedTrackingYear]);

  // Scroll the editor into view after a matrix-driven selection, once the selection resolves (the
  // cross-franchise path applies the year asynchronously). The flag keeps manual picker changes
  // from moving the viewport.
  useEffect(() => {
    if (pendingScrollRef.current && selectedComCode && selectedTrackingYear && editorRef.current) {
      editorRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
      pendingScrollRef.current = false;
    }
  }, [selectedComCode, selectedTrackingYear]);

  // Shared post-mutation refresh: any bucket change can move a calendar into or
  // out of the "Calendars worth reviewing" queue (its audit-based config-flag rule),
  // so a mutation must refresh BOTH the bucket table and the review queue. Used by
  // Add / Delete / rename / (de)activate. Reorder also routes here -- it can't
  // change the queue's qualification, but a redundant queue refetch is harmless
  // and keeps the refresh single-sourced rather than duplicated per child.
  const handleBucketsMutated = () => {
    loadBuckets();
    loadReviewList();
    loadMatrix();
  };

  // After a successful clone or start-with-defaults: the just-configured
  // year's has_buckets needs to flip to true, the (now non-empty) bucket table
  // needs to render in place of the empty state, and the review queue may gain a
  // >5-bucket calendar.
  const handleConfigured = () => {
    loadTrackingYears();
    handleBucketsMutated();
  };

  // After a bulk roll-forward: refresh the review list (new target-year splits may
  // appear) and, if a franchise is selected, its year list (the new target year
  // now has buckets).
  const handleRolledForward = () => {
    loadReviewList();
    loadMatrix();
    if (selectedComCode) loadTrackingYears();
  };

  const handleComCodeChange = (event, newValue) => {
    pendingYearRef.current = null;
    setSelectedComCode(newValue ? newValue.comcode : null);
  };

  // Jump straight to a calendar from the review list. If the franchise is
  // already selected, just set the year (its options are loaded); otherwise
  // stash the year and let loadTrackingYears apply it once options arrive.
  const handleReviewSelect = (comCode, trackingYear) => {
    if (comCode === selectedComCode) {
      setSelectedTrackingYear(trackingYear);
    } else {
      pendingYearRef.current = trackingYear;
      setSelectedComCode(comCode);
    }
    // Its job is done for this pick — collapse so the grid below is visible
    // without scrolling past the list.
    setReviewExpanded(false);
  };

  const handleTrackingYearChange = (event) => {
    setSelectedTrackingYear(event.target.value);
  };

  // Jump to a calendar from the overview matrix -- same mechanism as the review list. If the
  // franchise is already selected, set the year; otherwise stash it and let loadTrackingYears apply
  // it once that franchise's year options arrive.
  const handleMatrixSelect = (comCode, trackingYear) => {
    // Collapse the browse matrix and request a scroll to the editor once the selection resolves.
    setMatrixExpanded(false);
    pendingScrollRef.current = true;
    if (comCode === selectedComCode) {
      setSelectedTrackingYear(trackingYear);
    } else {
      pendingYearRef.current = trackingYear;
      setSelectedComCode(comCode);
    }
  };

  const selectedFranchiseOption =
    comcodes.find((c) => c.comcode === selectedComCode) || null;

  // Newest-first: the year an auditor is most likely to want (the upcoming
  // or current one) should be at the top, not buried below years of history.
  const sortedTrackingYears = [...trackingYears].sort((a, b) => b.trackingyear - a.trackingyear);

  const trackingYearMenuItems = sortedTrackingYears.map((y) => (
    <MenuItem key={y.trackingyear} value={y.trackingyear}>
      {y.trackingyear}{y.has_buckets ? "" : " (no buckets yet)"}
    </MenuItem>
  ));

  const reviewRows = reviewList.map((cal) => {
    const franchise = comcodes.find((c) => c.comcode === cal.com_code);
    const franchiseLabel = franchise && franchise.franchise_name ? franchise.franchise_name : cal.com_code;
    return (
      <ListItemButton
        key={`${cal.com_code}-${cal.trackingyear}`}
        onClick={() => handleReviewSelect(cal.com_code, cal.trackingyear)}
      >
        <ListItemText
          primary={`${franchiseLabel} — ${cal.trackingyear}`}
          secondary={`${cal.active_bucket_count} buckets`}
        />
        {cal.flagged_bucket_count > 0 && (
          <Chip size="small" color="warning" variant="outlined" label={`${cal.flagged_bucket_count} to check`} />
        )}
      </ListItemButton>
    );
  });

  return (
    <Box sx={{ p: 2 }}>
      {/* Top toolbar: franchise/year pickers are the primary control (left);
          the review list is a secondary, collapsed-by-default utility (right)
          so it reads as a quick filter into the grid, not the main feature. */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          gap: 2,
          mb: 3,
          flexWrap: "wrap",
        }}
      >
        <Box sx={{ display: "flex", gap: 2, flex: "1 1 320px", minWidth: 320, maxWidth: 600 }}>
          <Autocomplete
            sx={{ flex: 1 }}
            options={comcodes}
            getOptionLabel={(option) =>
              option.franchise_name ? `${option.franchise_name} (${option.comcode})` : option.comcode
            }
            isOptionEqualToValue={(option, value) => option.comcode === value.comcode}
            value={selectedFranchiseOption}
            onChange={handleComCodeChange}
            renderInput={(params) => <TextField {...params} label="Franchise" />}
          />

          <FormControl sx={{ minWidth: 180 }} disabled={!selectedComCode || loadingYears}>
            <InputLabel id="season-sequence-year-label">Tracking Year</InputLabel>
            <Select
              labelId="season-sequence-year-label"
              label="Tracking Year"
              value={selectedTrackingYear}
              onChange={handleTrackingYearChange}
            >
              {trackingYearMenuItems}
            </Select>
          </FormControl>
        </Box>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 1, alignItems: "flex-end", flex: "0 0 auto" }}>
          <Button variant="outlined" size="small" onClick={() => setRollForwardOpen(true)}>
            Roll forward to next year…
          </Button>
          {reviewList.length > 0 && (
            <Accordion
              expanded={reviewExpanded}
              onChange={(event, isExpanded) => setReviewExpanded(isExpanded)}
              sx={{ width: 360, flex: "0 0 auto" }}
            >
              <AccordionSummary expandIcon={<Typography aria-hidden>▾</Typography>}>
                <Typography variant="body2">Calendars worth reviewing ({reviewList.length})</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="caption" color="text.secondary">
                  Franchise-years with at least one bucket whose Maps To disagrees with the audited
                  Tracking Season of its assigned seasons. "pt=0 check" / "unaudited" are informational
                  and don't place a calendar here.
                </Typography>
                <List dense>{reviewRows}</List>
              </AccordionDetails>
            </Accordion>
          )}
        </Box>
      </Box>

      {loadingYears && <CircularProgress size={24} />}
      {yearsError && <Typography color="error">{yearsError}</Typography>}

      {/* Selected Franchise Calendar editor -- the page's primary purpose, ABOVE the browse matrix.
          Wrapped in a ref so a matrix-cell click can scroll it into view when it would be offscreen. */}
      <Box ref={editorRef}>
        {selectedComCode && selectedTrackingYear && (
          <>
            {loadingBuckets && <CircularProgress size={24} />}
            {bucketsError && <Typography color="error">{bucketsError}</Typography>}

            {!loadingBuckets && !bucketsError && buckets.length === 0 && (
              <SeasonSequenceEmptyState
                key={`${selectedComCode}-${selectedTrackingYear}`}
                comCode={selectedComCode}
                trackingYear={selectedTrackingYear}
                trackingYears={trackingYears}
                onConfigured={handleConfigured}
              />
            )}

            {!loadingBuckets && !bucketsError && buckets.length > 0 && (
              <SeasonSequenceBucketList
                key={`${selectedComCode}-${selectedTrackingYear}`}
                comCode={selectedComCode}
                trackingYear={selectedTrackingYear}
                buckets={buckets}
                onBucketsChanged={handleBucketsMutated}
              />
            )}
          </>
        )}
      </Box>

      {/* Browse Franchise Calendars -- collapsible overview/navigator BELOW the editor. Clicking a
          cell sets the Franchise + Tracking Year filters (loading the editor above) and collapses
          this section. The pickers remain fully usable on their own. */}
      <SeasonSequenceMatrix
        calendars={matrix}
        comcodes={comcodes}
        selectedComCode={selectedComCode}
        selectedTrackingYear={selectedTrackingYear}
        onSelectCell={handleMatrixSelect}
        expanded={matrixExpanded}
        onToggleExpanded={(event, isExpanded) => setMatrixExpanded(isExpanded)}
      />

      <RollForwardDialog
        open={rollForwardOpen}
        onClose={() => setRollForwardOpen(false)}
        comcodes={comcodes}
        selectedComCode={selectedComCode}
        onApplied={handleRolledForward}
      />
    </Box>
  );
}
